import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BurgerMenu } from '@/components/kit';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { useAuthStore } from '@/stores/useAuthStore';
import { useFeedbackStore } from '@/stores/useFeedbackStore';
import { Permission } from '@/types/user';

/** Which door the screen opening the menu IS, so it can wear "Home". */
export type NurseDoor = 'day' | 'stock';

/**
 * THE NURSE'S ☰ — `maie-phone-nurse/g_n0`.
 *
 * TWO DOORS, AND THE CHART IS NOT ONE. The board's ghost card is explicit:
 * "tasks open from My day; the chart never opens at all (the safety band
 * only)". A nurse reads allergies and the band on the task she is doing — the
 * record is not a place she browses, and a menu entry would say it was.
 *
 * THE STOCK-MANAGER HAT IS FOLDED, exactly as the doctor's manager hat is: a
 * nurse who also runs the shelf gets those seven doors as one collapsed group,
 * because it is a second job rather than seven more nursing doors. Absent
 * entirely when this login does not hold the role.
 */
export function NurseMenu({
  door,
  onClose,
  counts,
}: {
  door: NurseDoor;
  onClose: () => void;
  counts?: Partial<Record<NurseDoor, string>>;
}) {
  const navigate = useNavigate();
  const { person, branch } = useShellIdentity();
  const logout = useAuthStore((s) => s.logout);
  const roleCheck = useAuthStore((s) => s.roleCheck);
  const openFeedback = useFeedbackStore((s) => s.setOpen);
  const [groupOpen, setGroupOpen] = useState(false);

  /* The stock-manager role has no UserRole member — the frontend enum predates
     it and maps anything it does not know to a fallback. The permission it
     grants is what actually decides whether those doors work, so that is what
     is asked. */
  const alsoStock = roleCheck.hasPermission(Permission.STOCK_WRITE);

  const go = (to: string) => {
    onClose();
    navigate(to);
  };
  const home = <span className="chip chip-brand chip-sm">Home</span>;

  return (
    <BurgerMenu
      open
      onClose={onClose}
      roleChip="Nurse"
      doors={[
        {
          key: 'day',
          icon: 'list-check',
          label: 'My day',
          sub: counts?.day,
          badge: door === 'day' ? home : undefined,
          onSelect: () => go('/nurse/day'),
        },
        {
          key: 'stock',
          icon: 'packages',
          label: 'Stock',
          sub: counts?.stock ?? 'the shelf, read-only',
          badge: door === 'stock' ? home : undefined,
          onSelect: () => go('/nurse/stock'),
        },
      ]}
      group={
        alsoStock
          ? {
              head: 'Stock manager',
              summary: 'Stock · Catalog · Receive · Orders · Count · Transfers · Directory',
              caption: '7 doors · folded — tap to open',
              items: [
                { key: 's-shelf', label: 'Stock', onSelect: () => go('/stock-manager/shelf') },
                { key: 's-catalog', label: 'Catalog', onSelect: () => go('/stock-manager/catalog') },
                { key: 's-receive', label: 'Receive', onSelect: () => go('/stock-manager/receive') },
                { key: 's-orders', label: 'Orders', onSelect: () => go('/stock-manager/orders') },
                { key: 's-count', label: 'Count', onSelect: () => go('/stock-manager/count') },
                { key: 's-transfers', label: 'Transfers', onSelect: () => go('/stock-manager/transfers') },
                { key: 's-directory', label: 'Directory', onSelect: () => go('/directory') },
              ],
            }
          : undefined
      }
      groupOpen={groupOpen}
      onToggleGroup={() => setGroupOpen((v) => !v)}
      note={
        <>
          Tasks open from My day; the chart never opens at all — the safety band only.
        </>
      }
      account={{
        name: person?.name ?? 'This account',
        initials: person?.initials ?? 'N',
        caption: 'account — devices · notifications',
        onSelect: () => go('/settings'),
      }}
      branch={branch}
      onAppearance={() => go('/settings#appearance')}
      onFeedback={() => {
        onClose();
        openFeedback(true);
      }}
      onSignOut={() => void logout()}
    />
  );
}
